import {
    SOLD_PDP_YMAL_ITEMS_PER_ROW,
    SOLD_PDP_YMAL_MAXIMUM_ROWS,
    PDP_SPONSORED_ROW_INDICES,
} from '../PdpRespSimilarItems/pdpRespSimilarItemsConstants.ts';

export type PdpSimilarItemsSponsoredMetadata = {
    impressionTrackerLink?: string | null;
    clickTrackerLink?: string | null;
};

export type PdpSimilarItemsMergedEntry<T> = {
    item: T;
    /** null for organic tiles; {} for sponsored tiles without tracker metadata. */
    sponsoredMetadata: PdpSimilarItemsSponsoredMetadata | null;
};

type ItemWithServiceId = { serviceId?: string | null };

type MergeArgs<TOrganic extends ItemWithServiceId, TSponsored extends ItemWithServiceId> = {
    organicItems: readonly TOrganic[];
    sponsoredItems: readonly TSponsored[];
    metadataByItemId: Readonly<Record<string, PdpSimilarItemsSponsoredMetadata>>;
    itemsPerRow?: number;
    sponsoredRowIndices?: readonly number[];
    maxRows?: number;
};

const toOrganicEntry = <T>(item: T): PdpSimilarItemsMergedEntry<T> => ({
    item,
    sponsoredMetadata: null,
});

// Merge places SLs on even rows and stops after one SL row past the last organic
// row, so placeholders cannot assume every eligible SL id will render.
export function getMergeableSponsoredPlaceholderCount({
    organicCount,
    sponsoredPoolCount,
    itemsPerRow,
    maxRows,
}: {
    organicCount: number;
    sponsoredPoolCount: number;
    itemsPerRow: number;
    maxRows: number;
}): number {
    if (organicCount <= 0 || sponsoredPoolCount < itemsPerRow) {
        return 0;
    }
    const organicRows = Math.ceil(organicCount / itemsPerRow);
    const sponsoredPoolRows = Math.floor(sponsoredPoolCount / itemsPerRow);
    const maxSponsoredRows = Math.min(
        sponsoredPoolRows,
        Math.max(0, maxRows - organicRows),
        organicRows + 1
    );
    return maxSponsoredRows * itemsPerRow;
}

/**
 * Interleaves full sponsored rows with organic rows: SL5, ML5, SL5, ML5…
 * Partial sponsored rows are discarded. Stops at maxRows (default 16) or when
 * inventory runs out. Does not recycle sponsored items.
 */
export function mergePdpSimilarItemsAndSponsoredItems<
    TOrganic extends ItemWithServiceId,
    TSponsored extends ItemWithServiceId,
>({
    organicItems,
    sponsoredItems,
    metadataByItemId,
    itemsPerRow = SOLD_PDP_YMAL_ITEMS_PER_ROW,
    sponsoredRowIndices = PDP_SPONSORED_ROW_INDICES,
    maxRows = SOLD_PDP_YMAL_MAXIMUM_ROWS,
}: MergeArgs<TOrganic, TSponsored>): PdpSimilarItemsMergedEntry<TOrganic | TSponsored>[] {
    const usableSponsoredCount = sponsoredItems.length - (sponsoredItems.length % itemsPerRow);
    const usableSponsoredItems = sponsoredItems.slice(0, usableSponsoredCount);

    if (organicItems.length === 0) {
        return [];
    }

    if (usableSponsoredItems.length === 0) {
        return organicItems.slice(0, maxRows * itemsPerRow).map(toOrganicEntry);
    }

    const sponsoredRowIndexSet = new Set(sponsoredRowIndices);
    const mergedEntries: PdpSimilarItemsMergedEntry<TOrganic | TSponsored>[] = [];

    let organicIndex = 0;
    let sponsoredIndex = 0;
    let currRow = 0;

    const appendSponsoredRow = (): void => {
        for (let i = 0; i < itemsPerRow; i++) {
            const item = usableSponsoredItems[sponsoredIndex + i];
            mergedEntries.push({
                item,
                // Always mark the tile as sponsored, even when tracker metadata is missing.
                sponsoredMetadata: (item.serviceId && metadataByItemId[item.serviceId]) || {},
            });
        }
        sponsoredIndex += itemsPerRow;
    };

    const appendOrganicRow = (): void => {
        const rowEnd = Math.min(organicIndex + itemsPerRow, organicItems.length);
        for (let i = organicIndex; i < rowEnd; i++) {
            mergedEntries.push(toOrganicEntry(organicItems[i]));
        }
        organicIndex = rowEnd;
    };

    while (
        currRow < maxRows &&
        (organicIndex < organicItems.length || sponsoredIndex < usableSponsoredItems.length)
    ) {
        const canPlaceSponsoredRow =
            sponsoredRowIndexSet.has(currRow) &&
            sponsoredIndex + itemsPerRow <= usableSponsoredItems.length;

        if (canPlaceSponsoredRow) {
            appendSponsoredRow();
        } else if (organicIndex < organicItems.length) {
            appendOrganicRow();
        } else {
            // No organic left and not enough SLs for another full row — stop.
            break;
        }

        currRow++;
    }

    return mergedEntries;
}
